import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { Brand, SocialType } from '@app/shared/interfaces/brand.interface';
import { BrandsService } from '@app/shared/services/brands.service';
import { PickerResponse } from 'filestack-js';

@Component({
  selector: 'sbnb-brand-edit',
  templateUrl: './brand-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrandEditComponent {
  private readonly brandService = inject(BrandsService);

  getSocialTypeMetadata(type: SocialType) {
    return this.brandService.getSocialTypeMetadata(type);
  }

  loading = signal(true);
  saving = signal(false);
  brand = signal<Brand | null>(null);
  recentlySaved = signal(false);

  allSocialTypes = signal(Object.values(SocialType));

  // Get available social types for adding a new social (excluding existing ones)
  getNextAvailableSocialType() {
    return this.allSocialTypes().filter((type) => !this.brand()?.socials.some((social) => social.type === type));
  }

  // Get available social types for a specific social entry (including its current type but excluding other used types)
  getAvailableSocialTypesForSocial(currentType: SocialType) {
    return this.allSocialTypes().filter(
      (type) => type === currentType || !this.brand()?.socials.some((social) => social.type === type)
    );
  }

  constructor() {
    this.brandService.getBrand().subscribe((brand) => {
      this.brand.set(brand.data);
      this.loading.set(false);
    });
  }

  addSocial() {
    const currentBrand = this.brand()!;
    const availableTypes = this.getNextAvailableSocialType();
    const newSocial = { type: availableTypes[0], handle: '' };

    this.brand.set({
      ...currentBrand,
      socials: [...currentBrand.socials, newSocial],
    });
  }

  deleteSocial(type: SocialType) {
    const currentBrand = this.brand()!;
    this.brand.set({
      ...currentBrand,
      socials: currentBrand.socials.filter((social) => social.type !== type),
    });
  }

  logoUploadSuccess(event: PickerResponse) {
    const currentBrand = this.brand()!;

    this.brand.set({
      ...currentBrand,
      logo_url: event.filesUploaded[0].url,
    });
  }

  logoRemove() {
    const currentBrand = this.brand()!;
    this.brand.set({
      ...currentBrand,
      logo_url: null,
    });
  }

  iconUploadSuccess(event: PickerResponse) {
    const currentBrand = this.brand()!;
    this.brand.set({
      ...currentBrand,
      icon_url: event.filesUploaded[0].url,
    });
  }

  iconRemove() {
    const currentBrand = this.brand()!;
    this.brand.set({
      ...currentBrand,
      icon_url: null,
    });
  }

  save() {
    this.saving.set(true);

    this.brandService.updateBrand(this.brand()).subscribe({
      next: (brand) => {
        this.brand.set(brand.data);
        this.saving.set(false);
        this.recentlySaved.set(true);

        setTimeout(() => {
          this.recentlySaved.set(false);
        }, 2000);
      },
      error: (error) => {
        this.saving.set(false);
      },
    });
  }
}
